import { FinancingSettings } from "../types/financing";

export function calculateFinancingAmounts(
  projectCost: number,
  ltcPct: number
): { loanAmount: number; equityRequired: number } {
  const cost = projectCost || 0;
  const pct = ltcPct || 0;

  const loanAmount = cost * (pct / 100);
  const equityRequired = cost - loanAmount;

  return { loanAmount, equityRequired };
}

export const buildDebtSchedule = (
  financingSettings: any,
  projectCost: number
) => {
  const ltv = (financingSettings?.debtToEquityRatio || 60) / 100; // Loan to Value
  const annualRate = (financingSettings?.interestRate || 5) / 100; // Ставка (5%)
  const termYears = financingSettings?.loanTermYears || 20; // Срок (20 лет)

  const loanAmount = projectCost * ltv;

  if (loanAmount <= 0 || termYears <= 0) {
    return {
      annualDebtService: 0,
      monthlyPayment: 0,
      months: [],
    };
  }

  // 3. Расчет аннуитетного платежа (PMT)
  const monthlyRate = annualRate / 12;
  const numPayments = termYears * 12;

  // Формула PMT: P * (r(1+r)^n) / ((1+r)^n - 1)
  const monthlyPayment =
    (loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments))) /
    (Math.pow(1 + monthlyRate, numPayments) - 1);

  const annualDebtService = monthlyPayment * 12;

  const months = [];
  let currentBalance = loanAmount;

  for (let i = 0; i < numPayments; i++) {
    if (currentBalance <= 0) break;

    const interestPayment = currentBalance * monthlyRate;
    let principalPayment = monthlyPayment - interestPayment;

    if (i === numPayments - 1) {
      principalPayment = currentBalance;
    } else if (principalPayment > currentBalance) {
      principalPayment = currentBalance;
    }

    currentBalance -= principalPayment;

    months.push({
      interest: interestPayment,
      principal: principalPayment,
      balance: Math.max(0, currentBalance),
      payment: monthlyPayment,
    });
  }

  return {
    annualDebtService,
    monthlyPayment,
    months,
  };
};

export function monthlyDebtServiceByYear(
  settings: FinancingSettings,
  projectCost: number,
  year: number
): number {
  const schedule = buildDebtSchedule(settings, projectCost);

  if (schedule.monthlyPayment) {
    return schedule.monthlyPayment;
  }

  const startMonth = (year - 1) * 12;
  const endMonth = year * 12;

  const yearPayments = schedule.months.slice(startMonth, endMonth);

  if (yearPayments.length === 0) return 0;

  return (
    yearPayments.reduce((sum, m) => sum + m.payment, 0) / yearPayments.length
  );
}
export function loanBalanceAtEndOfYear(
  settings: FinancingSettings,
  projectCost: number,
  year: number
): number {
  const schedule = buildDebtSchedule(settings, projectCost);
  const monthIndex = year * 12 - 1;

  if (monthIndex >= schedule.months.length) {
    return 0;
  }

  return schedule.months[monthIndex]?.balance || 0;
}
